import { useState } from 'react';

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { Icon } from '@karehero/llama/components/atoms/Icon/Icon';

import { inFrameDialogProps } from '@/designs-dev/device/inFrameDialog';
import {
  CARE_CIRCLE,
  type ActionItem,
  type Call,
} from '@/cam-admin/control-panel/fixtures';

/**
 * Create-an-action modal, opened from a call drawer's Linked actions section. A
 * trimmed action form — no app-section link — so the CAM stays on the call.
 */
const LinkedActionModal = ({
  call,
  inFrame,
  onCreate,
  onClose,
}: {
  call: Call;
  inFrame?: boolean;
  onCreate: (fields: Partial<ActionItem>) => void;
  onClose: () => void;
}) => {
  const [title, setTitle] = useState('');
  const [note, setNote] = useState('');
  const [assignee, setAssignee] = useState('');
  const [due, setDue] = useState('');
  const [time, setTime] = useState('');

  const valid = title.trim().length > 0;

  const submit = () =>
    onCreate({
      title: title.trim(),
      note: note.trim() || undefined,
      assignee: assignee || undefined,
      due: due.trim() || undefined,
      time: time.trim() || undefined,
    });

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth='xs'
      fullWidth
      {...(inFrame ? inFrameDialogProps : {})}
    >
      <DialogTitle>
        <Stack
          sx={{ alignItems: 'center', justifyContent: 'space-between' }}
          direction='row'
        >
          <Typography variant='h3' color='primary.main'>
            Create action
          </Typography>
          <IconButton aria-label='close' onClick={onClose}>
            <Icon icon='xmark' fontSize={18} />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent>
        <Stack spacing={2}>
          <Typography variant='subtitle1'>
            {`Create an action linked to ${call.title} with ${call.withWho} on ${call.date}.`}
          </Typography>

          <TextField
            fullWidth
            label='Title'
            placeholder='e.g. Review your shortlist'
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
          <TextField
            fullWidth
            multiline
            minRows={3}
            label='Note'
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
          <TextField
            slotProps={{ select: { displayEmpty: true } }}
            select
            fullWidth
            label='Assignee'
            value={assignee}
            onChange={(e) => setAssignee(e.target.value)}
            helperText='Who should complete this task?'
          >
            <MenuItem value=''>Unassigned</MenuItem>
            {CARE_CIRCLE.carers.map((c) => (
              <MenuItem key={c.name} value={c.name}>
                {c.name}
              </MenuItem>
            ))}
          </TextField>

          <Stack direction='row' spacing={2}>
            <TextField
              fullWidth
              label='Date'
              placeholder='e.g. Sat 14 Jun'
              value={due}
              onChange={(e) => setDue(e.target.value)}
            />
            <TextField
              fullWidth
              label='Time'
              placeholder='e.g. 14:00'
              value={time}
              onChange={(e) => setTime(e.target.value)}
            />
          </Stack>
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button variant='outlined' color='primary' onClick={onClose}>
          Discard
        </Button>
        <Button
          variant='contained'
          color='primary'
          disabled={!valid}
          onClick={submit}
        >
          Create action
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LinkedActionModal;
